import { ClockIcon, HomeIcon, MapPinIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import React, { ReactNode } from 'react';
import { Link } from 'hotel/navigation';
import { twMerge } from 'tailwind-merge';

type CardProps = Partial<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
> & {
  footer?: ReactNode | ReactNode[];
  footerClassName?: string;
  contentClassName?: string;
};

export default function Card({
  className,
  footer,
  children,
  footerClassName,
  contentClassName,
  ...props
}: CardProps) {
  return (
    <div
      className={twMerge(
        'bg-white h-full rounded-lg border border-backgroundDarkGray flex flex-col overflow-hidden',
        className,
      )}
      {...props}
    >
      <div className={twMerge('p-5', contentClassName)}>{children}</div>
      {footer && (
        <div
          className={twMerge(
            'mt-auto items-end bg-backgroundLightGray p-5',
            footerClassName,
          )}
        >
          {footer}
        </div>
      )}
    </div>
  );
}

interface LargeStatCardProps {
  headline: string;
  headlineClassName?: string;
  content: ReactNode | ReactNode[];
  link: string;
  CardIcon: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, 'ref'> & {
      title?: string;
      titleId?: string;
    } & React.RefAttributes<SVGSVGElement>
  >;
  iconClassname?: string;
}

export function LargeStatCard({
  headline,
  headlineClassName,
  link,
  content,
  CardIcon,
  iconClassname,
}: LargeStatCardProps) {
  return (
    <Card
      footer={
        <Link to={link} className="block text-sm">
          View all
        </Link>
      }
    >
      <div className="flex flex-col items-start">
        <h2
          className={classNames(
            'mb-5  text-base text-inactiveGray',
            headlineClassName,
          )}
        >
          {headline}
        </h2>
        <div className="flex flex-row items-center">
          <div className="mr-5 rounded-md bg-primaryBlue p-3 text-white">
            <CardIcon
              className={classNames('size-7', iconClassname)}
              aria-hidden="true"
            />
          </div>
          <div>{content}</div>
        </div>
      </div>
    </Card>
  );
}

interface GuestCardProps {
  count: number;
  link: string;
  title: string;
  titleColor: string;
  iconBgColor: string;
}

export function GuestCard({
  count,
  link,
  title,
  titleColor,
  iconBgColor,
}: GuestCardProps) {
  return (
    <Card
      footer={
        <Link to={link} className="block text-sm">
          View all
        </Link>
      }
    >
      <div className="flex flex-col items-start">
        <h2 className={classNames('mb-5 text-base', titleColor)}>{title}</h2>
        <div className="flex flex-row items-center">
          <div
            className={classNames(
              'mr-5 rounded-md p-2 text-white',
              iconBgColor,
            )}
          >
            <HomeIcon
              className={classNames('size-5', iconBgColor)}
              aria-hidden="true"
            />
          </div>
          <div className="text-xl font-bold">{count}</div>
        </div>
      </div>
    </Card>
  );
}

interface InHouseCardProps {
  count: number;
  link: string;
  className?: string;
}

export function InHouseCard({
  count,
  link,
  className,
  ...props
}: InHouseCardProps) {
  return (
    <div
      className={twMerge(
        'bg-white h-full rounded-lg border border-backgroundDarkGray flex flex-col overflow-hidden',
        className,
      )}
      {...props}
    >
      <div className="p-5">
        <h3 className="mb-6 font-oswald text-lg font-semibold text-green-500 sm:text-xl">
          In House
        </h3>
        <div className="flex items-center">
          <div className="mr-5 rounded-md bg-green-500 p-2 text-white">
            <HomeIcon className="size-5" aria-hidden="true" />
          </div>
          <div className="text-xl font-bold">{count}</div>
        </div>
      </div>
      <div className="mt-auto items-end bg-backgroundLightGray p-5">
        <Link
          to={link}
          className="block text-sm text-primaryBlue hover:text-primaryDarkBlue"
        >
          View All
        </Link>
      </div>
    </div>
  );
}

interface UpcomingCardProps {
  count: number;
  link: string;
  className?: string;
}

export function UpcomingCard({
  count,
  link,
  className,
  ...props
}: UpcomingCardProps) {
  return (
    <div
      className={twMerge(
        'bg-white h-full rounded-lg border border-backgroundDarkGray flex flex-col overflow-hidden',
        className,
      )}
      {...props}
    >
      <div className="p-5">
        <h3 className="mb-6 font-oswald text-lg font-semibold text-blue-500 sm:text-xl">
          Upcoming
        </h3>
        <div className="flex items-center">
          <div className="mr-5 rounded-md bg-blue-500 p-2 text-white">
            <ClockIcon className="size-5" aria-hidden="true" />
          </div>
          <div className="text-xl font-bold">{count}</div>
        </div>
      </div>
      <div className="mt-auto items-end bg-backgroundLightGray p-5">
        <Link
          to={link}
          className="block text-sm text-primaryBlue hover:text-primaryDarkBlue"
        >
          View All
        </Link>
      </div>
    </div>
  );
}

interface ListCardProps {
  title: string;
  titleColor?: string;
  children: ReactNode | ReactNode[];
  className?: string;
  footerLink?: string;
  footerText?: string;
}

export function ListCard({
  title,
  titleColor = 'text-primaryBlue',
  children,
  className,
  footerLink,
  footerText = 'View All',
  ...props
}: ListCardProps) {
  return (
    <div
      className={twMerge(
        'bg-white h-full rounded-lg border border-backgroundDarkGray flex flex-col overflow-hidden',
        className,
      )}
      {...props}
    >
      <div className="p-5">
        <h3
          className={`mb-6 font-oswald text-lg font-semibold sm:text-xl ${titleColor}`}
        >
          {title}
        </h3>
        <div className="flex flex-col gap-4">{children}</div>
      </div>
      {footerLink && (
        <div className="mt-auto items-end bg-backgroundLightGray p-5">
          <Link
            to={footerLink}
            className="block text-sm text-primaryBlue hover:text-primaryDarkBlue"
          >
            {footerText}
          </Link>
        </div>
      )}
    </div>
  );
}

interface ListCardItemProps {
  description: string;
  number: number;
  link?: string;
  icon: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, 'ref'> & {
      title?: string;
      titleId?: string;
    } & React.RefAttributes<SVGSVGElement>
  >;
  iconBgColor: string;
}

export function ListCardItem({
  description,
  number,
  link,
  icon: Icon,
  iconBgColor,
}: ListCardItemProps) {
  return (
    <div className="flex items-center justify-between border-t pt-4 first:border-t-0 first:pt-0">
      <div className="flex items-center">
        <div
          className={classNames('mr-5 rounded-md p-2 text-white', iconBgColor)}
        >
          <Icon className="size-5" aria-hidden="true" />
        </div>
        <span className="mr-3">{description}</span>
        <span className="text-xl font-bold">{number}</span>
      </div>
      {link && (
        <Link to={link} className="text-sm">
          View all
        </Link>
      )}
    </div>
  );
}

interface SmallStatCardProps {
  headline: string;
  content: ReactNode | ReactNode[];
  link: string;
}

interface CityCardProps {
  rank: number;
  city: string;
  state: string;
  inHouse: number;
  upcoming: number;
  link: string;
  className?: string;
}

export function CityCard({
  rank,
  city,
  state,
  inHouse,
  upcoming,
  link,
  className,
}: CityCardProps) {
  return (
    <Card
      footer={
        <Link
          to={link}
          className="block rounded-md border p-2 text-center text-sm"
        >
          View guests in {city}
        </Link>
      }
      footerClassName="bg-white"
      className={twMerge('border-0', className)}
    >
      <div className="flex flex-col items-start justify-start">
        <div className="mb-2 flex flex-col items-start pb-2">
          <div className="mb-2 rounded-md text-secondaryBlue">
            <MapPinIcon className="size-7" aria-hidden="true" />
          </div>
          <h2 className="text-xl font-bold">{`${rank}. ${city}, ${state}`}</h2>
        </div>
        <div className="flex w-full">
          <div className="mr-6 flex flex-col items-start">
            <span className="text-sm font-semibold text-green-500">
              In-House
            </span>
            <div className="mt-2 flex items-center">
              <HomeIcon
                className="size-5 rounded-md bg-green-500 p-[0.2rem] text-white"
                aria-hidden="true"
              />
              <span className="ml-2">{inHouse}</span>
            </div>
          </div>
          <div className="flex flex-col items-start">
            <span className="text-sm font-semibold text-blue-500">
              Upcoming
            </span>
            <div className="mt-2 flex items-center">
              <ClockIcon
                className="size-5 rounded-md bg-blue-500 p-[0.2rem] text-white"
                aria-hidden="true"
              />
              <span className="ml-2">{upcoming}</span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

export function SmallStatCard({ headline, link, content }: SmallStatCardProps) {
  return (
    <Card
      footer={
        <Link to={link} className="block text-center text-sm">
          View all
        </Link>
      }
    >
      <div className="flex flex-col items-stretch justify-center text-center">
        <div className=" mb-2 flex flex-col items-center border-b border-backgroundDarkGray pb-2">
          <div className="mb-2 rounded-md bg-primaryBlue p-3 text-white">
            <HomeIcon className="size-6" aria-hidden="true" />
          </div>
          <h2 className="text-lg">{headline}</h2>
        </div>
        {content}
      </div>
    </Card>
  );
}
